/**
 * Cache utility functions for optimizing asset loading and caching
 */

/**
 * Adds cache parameters to S3 URLs to improve caching performance
 * 
 * NOTE: This function currently returns the URL as-is because HTTP cache headers
 * (Cache-Control, Expires, etc.) must be set by the server (S3 or CloudFront),
 * not by the client. The frontend cannot set these headers.
 * 
 * To fix PageSpeed cache warnings, configure the S3 bucket or CloudFront distribution
 * to send appropriate Cache-Control headers. Recommended settings:
 * - Images/Videos: Cache-Control: public, max-age=31536000, immutable
 * - Static assets: Cache-Control: public, max-age=86400
 * 
 * @param {string} url - The S3 URL
 * @param {Object} options - Cache options (kept for compatibility but not used)
 * @returns {string} - URL as-is since S3 cache headers need to be configured server-side
 */
export const addCacheHeaders = (url, options = {}) => {
  if (!url || typeof url !== 'string') return url;

  // Since we don't have access to S3 configuration, just return the URL as-is
  // The cache headers need to be configured at the S3/CloudFront level
  // See: https://docs.aws.amazon.com/AmazonS3/latest/userguide/UsingMetadata.html
  return url;
};

/**
 * Preloads critical assets for better performance
 * @param {Array} assets - Array of asset URLs to preload
 * @param {string} type - Asset type ('video', 'image', 'font', etc.)
 */
export const preloadAssets = (assets, type = 'image') => {
  if (!Array.isArray(assets) || assets.length === 0) return;

  // Batch DOM operations to avoid forced reflows
  requestAnimationFrame(() => {
    assets.forEach((asset, index) => {
      if (!asset) return;

      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = addCacheHeaders(asset);
      link.as = type;

      // Add priority for critical assets
      if (index === 0) {
        link.setAttribute('fetchpriority', 'high');
      }

      document.head.appendChild(link);
    });
  });
};

/**
 * Creates optimized video element props for better caching
 * @param {string} src - Video source URL
 * @param {Object} options - Video options
 * @returns {Object} - Optimized video props
 */
export const getOptimizedVideoProps = (src, options = {}) => {
  const {
    preload = 'metadata',
    loading = 'lazy',
    fetchpriority = 'auto',
    ...otherProps
  } = options;

  return {
    src: addCacheHeaders(src),
    preload,
    loading,
    fetchpriority,
    ...otherProps
  };
};

/**
 * Creates optimized image element props for better caching
 * @param {string} src - Image source URL
 * @param {Object} options - Image options
 * @returns {Object} - Optimized image props
 */
export const getOptimizedImageProps = (src, options = {}) => {
  const {
    loading = 'lazy',
    fetchpriority = 'auto',
    ...otherProps
  } = options;

  return {
    src: addCacheHeaders(src),
    loading,
    fetchpriority,
    ...otherProps
  };
};

/**
 * Critical assets that should be preloaded
 */
export const CRITICAL_ASSETS = {
  videos: [
    'https://remote-recruit-email-temp.s3.us-east-1.amazonaws.com/website_images/hire_talent.webm',
    'https://remote-recruit-email-temp.s3.us-east-1.amazonaws.com/website_images/mielat-video.webm',
    'https://remote-recruit-email-temp.s3.us-east-1.amazonaws.com/website_images/New%2BFeatures%2BVideo.webm'
  ],
  images: [
    'https://remote-recruit-email-temp.s3.us-east-1.amazonaws.com/website_images/post-job-img.webp',
    'https://remote-recruit-email-temp.s3.us-east-1.amazonaws.com/website_images/prescreen_cover_desktop_resized.webp',
    'https://remote-recruit-email-temp.s3.us-east-1.amazonaws.com/website_images/RR-big-logo.webp',
    'https://remote-recruit-email-temp.s3.us-east-1.amazonaws.com/website_images/SquarewhiteRR300.webp'
  ]
};

/**
 * Initialize preloading of critical assets
 */
export const initializeAssetPreloading = () => {
  // Preload critical videos
  preloadAssets(CRITICAL_ASSETS.videos, 'video');

  // Preload critical images
  preloadAssets(CRITICAL_ASSETS.images, 'image');
};
